import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/** Crossdock-forslag for én vare på ett lager (TW 36395306) — speiler CrossDockSuggestionDto. */
export interface CrossDockSuggestion {
  enabled: boolean;
  hasSuggestion: boolean;
  isAdvancedWarehouse: boolean;
  demandQty: number;
  suggestedCrossDockQty: number;
  numOfDays: number;
  defaultCrossDockBinCode?: string | null;
}

/** En vare med crossdock-restbehov i linjelisten — speiler CrossDockCandidateDto. */
export interface CrossDockCandidate {
  itemNo: string;
  demandQty: number;
  isAdvancedWarehouse: boolean;
  numOfDays: number;
}

export interface CrossDockPutAwayResult {
  success: boolean;
  errorMessage?: string | null;
  crossDockQuantity: number;
  remainingQuantity: number;
  remainingLineId?: number | null;
}

class CrossDockService {
  /** Kalles når en ankomstlinje aktiveres. Ett databaseoppslag. */
  async getSuggestion(warehouseId: string, itemNo: string, quantity: number, signal?: AbortSignal): Promise<CrossDockSuggestion> {
    const params = new URLSearchParams({ warehouseId, itemNo, quantity: String(quantity || 0) });
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/crossdock/suggestion?${params}`, { signal });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to get crossdock suggestion'));
    }
    return await response.json();
  }

  /** Varene i linjelisten som har restbehov på lageret. Ett databaseoppslag for alle varene. */
  async getCandidates(warehouseId: string, itemNos: string[], signal?: AbortSignal): Promise<CrossDockCandidate[]> {
    const response = await AuthService.authenticatedFetch(`${API_BASE_URL}/crossdock/candidates`, {
      method: 'POST',
      body: JSON.stringify({ warehouseId, itemNos }),
      signal
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Failed to get crossdock candidates'));
    }
    return await response.json();
  }

  /** Legger antallet av en ankomstlinje (status 20) på crossdock; resten blir ny ankomstlinje. */
  async putAway(batchId: string, lineId: number, binCode: string, quantity: number): Promise<CrossDockPutAwayResult> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/crossdock/${encodeURIComponent(batchId)}/lines/${lineId}/putaway`,
      { binCode, quantity }
    );
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(body?.errorMessage || extractApiError(body, response, 'Failed to put away to crossdock'));
    }
    return body;
  }
}

export const crossDockService = new CrossDockService();
